"use client";

import type { ReportData } from "./reportData";

interface AlignmentPlanSectionProps {
  report: ReportData;
}

const WEEK_COLORS = [
  { accent: "#dbb84d", bg: "rgba(219,184,77,0.08)", border: "rgba(219,184,77,0.25)", label: "Week 1" },
  { accent: "#7c3e61", bg: "rgba(124,62,97,0.12)", border: "rgba(124,62,97,0.35)", label: "Week 2" },
  { accent: "#dbb84d", bg: "rgba(0,18,88,0.3)", border: "rgba(219,184,77,0.2)", label: "Week 3" },
  { accent: "#7c3e61", bg: "rgba(54,23,64,0.5)", border: "rgba(124,62,97,0.3)", label: "Week 4" },
];

export default function AlignmentPlanSection({ report }: AlignmentPlanSectionProps) {
  const { alignmentPlan } = report;

  return (
    <section className="fade-in-up">
      {/* Section Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="section-number">3</div>
        <div>
          <h2
            className="text-xl md:text-2xl font-display text-white/95"
            style={{ fontWeight: 700 }}
          >
            30-Day Alignment Plan
          </h2>
          <p className="text-sm text-white/45 mt-0.5" style={{ fontWeight: 400 }}>
            Four weeks of practical, purposeful action toward your highest expression
          </p>
        </div>
      </div>

      {/* Intro */}
      <div
        className="rounded-xl p-5 mb-6"
        style={{
          background: "rgba(219,184,77,0.07)",
          border: "1px solid rgba(219,184,77,0.2)",
          borderLeft: "4px solid rgba(219,184,77,0.6)",
        }}
      >
        <p className="text-sm text-white/65 leading-relaxed" style={{ fontWeight: 400 }}>
          This plan is designed around your archetypal nature. Each week builds on the last, moving you from awareness to full embodiment. Take it one action at a time — consistency matters more than perfection.
        </p>
      </div>

      {/* Weeks Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {alignmentPlan.weeks.map((week, weekIndex) => {
          const colors = WEEK_COLORS[weekIndex] || WEEK_COLORS[0];
          return (
            <div
              key={weekIndex}
              className="rounded-xl p-6 relative overflow-hidden transition-all duration-200 hover:-translate-y-0.5"
              style={{
                background: colors.bg,
                border: `1px solid ${colors.border}`,
                boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
              }}
            >
              {/* Top accent bar */}
              <div
                className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl"
                style={{
                  background: `linear-gradient(90deg, ${colors.accent}, transparent)`,
                }}
              />

              {/* Week label + theme */}
              <div className="mb-5">
                <span
                  className="text-xs font-600 tracking-widest uppercase"
                  style={{ color: colors.accent, letterSpacing: "0.12em", fontWeight: 600 }}
                >
                  {colors.label}
                </span>
                <h3
                  className="text-lg font-display mt-1 text-white/92"
                  style={{ fontWeight: 700 }}
                >
                  {week.theme}
                </h3>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                {week.actions.map((action, actionIndex) => (
                  <div key={actionIndex} className="flex items-start gap-3">
                    <div className="action-dot flex-shrink-0">
                      {actionIndex + 1}
                    </div>
                    <p className="text-sm text-white/72 leading-relaxed pt-0.5" style={{ fontWeight: 400 }}>
                      {action}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Progress tracker hint */}
      <div
        className="mt-5 rounded-xl p-5 flex items-start gap-4"
        style={{
          background: "rgba(54,23,64,0.5)",
          border: "1px solid rgba(219,184,77,0.12)",
        }}
      >
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: "rgba(219,184,77,0.15)", border: "1px solid rgba(219,184,77,0.3)" }}
        >
          <span className="text-base">📌</span>
        </div>
        <p className="text-xs text-white/50 leading-relaxed" style={{ fontWeight: 400 }}>
          <span style={{ color: "rgba(219,184,77,0.8)", fontWeight: 600 }}>Tip:</span> Save or print this plan and check off one action each day. Small consistent steps create lasting transformation. You don't need to be perfect — you just need to keep moving.
        </p>
      </div>
    </section>
  );
}